// SYNCED WITH: VidaReactNative address-verification dialog copy — keep the
// member-facing strings in step. See SYNC.md. VRN currently inlines these
// (getWarningCopy in VerificationWarningDialog, describeFootnote in
// UndeliverableDialog); they consolidate into a matching module once VRN adopts
// the card UI. Divergence is a deliberate, coordinated change, not drift.
import type { UnitDeliverabilityWarning } from '../types';

type Translate = (text: string) => string;

/** Bucket-specific title + description; description adds consequence/action, not a restatement. */
export const getWarningCopy = (deliverability: UnitDeliverabilityWarning, t: Translate) => {
  switch (deliverability) {
    case 'deliverable_missing_unit':
      return {
        title: t('Apartment or unit may be missing'),
        description: t('Vida may not be able to reach you without it.'),
      };
    case 'deliverable_unnecessary_unit':
      return {
        title: t('Apartment or unit not needed'),
        description: t("This address doesn't use one, so you can remove it."),
      };
    case 'deliverable_incorrect_unit':
      return {
        title: t('Apartment or unit not recognized'),
        description: t('Double-check the apartment, suite, or unit number below.'),
      };
  }
};

/**
 * Maps a USPS DPV footnote code to a user-facing hint, or `null` if the code is
 * not actionable for the user. See Lob docs for the full code list.
 */
const describeFootnote = (code: string, t: Translate): string | null => {
  switch (code) {
    // Not actionable for the user — informational or positive-match codes:
    case 'AA': // matched
    case 'A1': // did not match (redundant with deliverability)
    case 'BB': // all elements valid
    case 'IA': // "Informed Address"
    case 'F1': // military
    case 'G1': // "General Delivery" for people without permanent addresses
    case 'U1': // unique ZIP for large organization, always valid
    case 'PB': // PO Box street address
    case 'R1': // CMRA (commercial mail receiving agency), private mailbox information is not present
    case 'RR': // CMRA (commercial mail receiving agency), private mailbox information is present
      return null;

    // Probably redundant with the UnitDeliverabilityWarning codes, but who knows:
    case 'CC':
      return t('The address is deliverable by removing the apartment or unit number.');
    case 'N1':
      return t('The address is missing an apartment or unit number.');
    case 'C1':
      return t('The provided apartment or unit number is invalid.');

    // Actually helpful codes:
    case 'TA':
      return t('The street address number has an extra letter.');
    case 'M1':
      return t('The street address is missing a number.');
    case 'M3':
      return t('The street address number is invalid.');
    case 'P1':
      return t('The P.O. box number is missing.');
    case 'P3':
      return t('The P.O. box number is invalid.');
    case 'R7':
      return t("This is a real address, but the postal service can't deliver mail here.");
    default:
      return null;
  }
};

/**
 * Per-footnote user-facing hints used **only for `undeliverable` addresses**.
 * Codes that indicate normal/successful matching, informational statuses, and
 * unrecognized codes are all filtered out — the `null` return does double duty.
 */
export const getFootnoteHints = (footnoteCodes: string[], t: Translate): string[] =>
  footnoteCodes
    .map((code) => describeFootnote(code, t))
    .filter((message): message is string => message !== null);
